/**
 * What worn armour and a held shield do to the sheet beyond their own Rule Elements (Foundry:
 * CharacterPF2e#createArmorStatistic, #prepareSaves, #prepareSkills and #prepareMovementData):
 * the `armor:*` roll options, the Strength-requirement option, the speed penalties, the AC item
 * bonus and Dexterity cap, the check penalty on Strength- and Dexterity-based skills, and the
 * resilient rune and bulwark trait on saves. The inventory (./inventory.ts) says which armour is
 * worn and which shield is held; `EquippedInput` remains as a direct engine input for tests.
 */
import type { Entry } from "../../shared/snapshot-format";
import { localize } from "./labels";
import { createModifier, type Modifier } from "./modifiers";
import type { EnginePhysical } from "./physical";
import { armorProficiencyRank } from "./proficiencies";
import { sluggify } from "./slug";
import type { EngineState } from "./state";
import type { RawPredicate } from "./predicate";
import { extractModifierAdjustments } from "./synthetics";
import type { Rank } from "./values";
import type { EquippedWeaponInput } from "./weapons";

/** Physical Entries fed to the engine directly (tests): the inventory (#11) is the app's source. */
export interface EquippedInput {
  /** The worn armour: an Entry of type `armor` from the equipment Pack. */
  armor?: Entry | null;
  /** The held shield: an Entry of type `shield`. */
  shield?: Entry | null;
  /** The weapons carried: held, worn or stowed, with the copy's runes. */
  weapons?: EquippedWeaponInput[];
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function armorCategory(armor: EnginePhysical): string | null {
  return typeof armor.system.category === "string" ? armor.system.category : null;
}

/** Roll options Foundry sets for worn armour: `self:armored`, and the armour's own `armor:*` options. */
export function setArmorRollOptions(state: EngineState): void {
  const armor = state.inventory.wornArmor;
  const category = armor ? armorCategory(armor) : null;
  state.rollOptions.set("all", "self:armored", !!armor && category !== "unarmored");
  if (!armor) return;
  for (const option of armor.getRollOptions("armor")) state.rollOptions.set("all", option);
  const potency = armor.runes.potency;
  if (potency) state.rollOptions.set("all", `armor:rune:potency:${potency}`);
}

/** After attribute modifiers are final: whether the armour's Strength threshold is met (Foundry sets it on the skill-check domains). */
export function setStrengthRequirementOption(state: EngineState): void {
  const requirement = num(state.inventory.wornArmor?.system.strength);
  if (requirement !== null && state.actor.system.abilities.str.mod >= requirement) {
    for (const domain of ["dex-skill-check", "str-skill-check"]) state.rollOptions.set(domain, "armor:strength-requirement-met");
  }
}

/**
 * The speed penalties of worn armour and a held shield (Foundry: CharacterPF2e#prepareMovementData).
 * Meeting the armour's Strength threshold reduces its penalty by 5 (never above 0); the hindering
 * trait's -5 is separate and always applies.
 */
export function equipmentSpeedModifiers(state: EngineState): Modifier[] {
  const modifiers: Modifier[] = [];
  const armor = state.inventory.wornArmor;
  if (armor) {
    const basePenalty = num(armor.system.speedPenalty) ?? 0;
    const strength = state.actor.system.abilities.str.mod;
    const requirement = num(armor.system.strength);
    const penalty = Math.min(requirement !== null && strength >= requirement ? Math.min(basePenalty + 5, 0) : basePenalty, 0);
    if (penalty) {
      modifiers.push(createModifier({ slug: "armor-speed-penalty", label: armor.displayName, type: "untyped", value: penalty, domains: ["all-speeds"], predicate: [{ nor: ["armor:ignore-speed-penalty"] }], source: armor.uuid, sourceName: armor.displayName }));
    }
    if (armor.traits.includes("hindering")) {
      modifiers.push(createModifier({ slug: "hindering", label: localize(state, "PF2E.TraitHindering", "Hindering"), type: "untyped", value: -5, domains: ["all-speeds"], source: armor.uuid, sourceName: armor.displayName }));
    }
  }
  const shield = state.inventory.heldShield;
  const shieldPenalty = shield ? (num(shield.system.speedPenalty) ?? 0) : 0;
  if (shield && shieldPenalty) {
    modifiers.push(createModifier({ slug: "shield-speed-penalty", label: shield.displayName, type: "untyped", value: shieldPenalty, domains: ["all-speeds"], predicate: [{ not: "self:shield:ignore-speed-penalty" }], source: shield.uuid, sourceName: shield.displayName }));
  }
  return modifiers;
}

/** The AC proficiency rank: the worn armour's category (or a custom proficiency covering it), unarmored otherwise. */
export function armorClassRank(state: EngineState): Rank {
  const armor = state.inventory.wornArmor;
  const defenses = state.actor.system.proficiencies.defenses;
  return armor ? armorProficiencyRank(defenses, { category: armorCategory(armor) }, armor.getRollOptions("item")) : (defenses.unarmored?.rank ?? 0);
}

/** The worn armour's Dexterity cap, as a cap source (Foundry adds it to the DexterityModifierCap synthetics). */
export function armorDexterityCap(state: EngineState): { value: number; source: string } | null {
  const armor = state.inventory.wornArmor;
  return armor ? { value: num(armor.system.dexCap) ?? 0, source: armor.displayName } : null;
}

/** Foundry's ArmorStatistic bonuses and penalties: the armour's item bonus, a shoddy penalty, the raised shield's circumstance bonus. */
export function armorClassModifiers(state: EngineState): Modifier[] {
  const armor = state.inventory.wornArmor;
  const modifiers: Modifier[] = [];
  const domains = ["all", "ac"];
  if (armor) {
    const slug = armor.baseType ?? armor.slug ?? sluggify(armor.name);
    const potency = armor.isInvested ? armor.runes.potency : 0;
    modifiers.push(createModifier({ slug, label: armor.displayName, type: "item", value: (num(armor.system.acBonus) ?? 0) + potency, adjustments: extractModifierAdjustments(state.synthetics.modifierAdjustments, domains, slug), source: armor.uuid, sourceName: armor.displayName }));
    if (armor.isShoddy) modifiers.push(createModifier({ slug: "shoddy", label: localize(state, "PF2E.Item.Physical.OtherTag.Shoddy", "Shoddy"), type: "item", value: -2, adjustments: extractModifierAdjustments(state.synthetics.modifierAdjustments, domains, "shoddy") }));
  }
  const shield = state.actor.system.attributes.shield;
  if (shield.raised && !shield.broken) {
    modifiers.push(createModifier({ slug: "raised-shield", label: shield.name, type: "circumstance", value: shield.ac, adjustments: extractModifierAdjustments(state.synthetics.modifierAdjustments, ["all", "dex-based", "ac"], "raised-shield") }));
  }
  return modifiers;
}

/** The armour check penalty on a Strength- or Dexterity-based skill (Foundry: CharacterPF2e#prepareSkills). */
export function armorCheckPenalty(state: EngineState, skillSlug: string, domains: string[]): Modifier | null {
  const armor = state.inventory.wornArmor;
  if (!armor || typeof armor.system.strength !== "number") return null;
  const checkPenalty = num(armor.system.checkPenalty) ?? 0;
  if (checkPenalty >= 0) return null;
  const predicate: RawPredicate = [{ nor: ["attack", "armor:ignore-check-penalty"] }];
  if (["acrobatics", "athletics"].includes(skillSlug)) predicate.push({ nor: ["armor:strength-requirement-met", "armor:trait:flexible"] });
  else if (skillSlug === "stealth" && armor.traits.includes("noisy")) predicate.push({ nand: ["armor:strength-requirement-met", "armor:ignore-noisy-penalty"] });
  else predicate.push({ not: "armor:strength-requirement-met" });
  return createModifier({ slug: "armor-check-penalty", label: localize(state, "PF2E.ArmorCheckPenalty", "Armor Check Penalty"), type: "untyped", value: checkPenalty, predicate, adjustments: extractModifierAdjustments(state.synthetics.modifierAdjustments, domains, "armor-check-penalty"), source: armor.uuid, sourceName: armor.displayName });
}

/** The resilient rune's item bonus to saves, and bulwark's bonus to Reflex against damaging effects. */
export function armorSaveModifiers(state: EngineState, save: string, domains: string[]): Modifier[] {
  const armor = state.inventory.wornArmor;
  if (!armor) return [];
  const modifiers: Modifier[] = [];
  const config = (armor.system.traits as { config?: { resilient?: unknown } } | undefined)?.config;
  const fromTraits = num(config?.resilient) ?? 0;
  const fromRunes = armor.isEquipped && armor.isInvested !== false ? armor.runes.resilient : 0;
  const resilient = Math.max(fromTraits, fromRunes);
  if (resilient) modifiers.push(createModifier({ slug: "resilient", label: armor.displayName, type: "item", value: resilient, adjustments: extractModifierAdjustments(state.synthetics.modifierAdjustments, domains, "resilient"), source: armor.uuid, sourceName: armor.displayName }));
  if (save === "reflex" && armor.traits.includes("bulwark")) {
    modifiers.push(createModifier({ slug: "bulwark", label: localize(state, "PF2E.TraitBulwark", "Bulwark"), type: "untyped", value: 3, predicate: ["damaging-effect"], adjustments: extractModifierAdjustments(state.synthetics.modifierAdjustments, domains, "bulwark"), source: armor.uuid, sourceName: armor.displayName }));
    // Bulwark replaces the Dexterity modifier against damaging effects.
    (state.synthetics.modifierAdjustments[save] ??= []).push({ slug: "dex", test: (options) => new Set(options).has("damaging-effect"), suppress: true, sourceName: localize(state, "PF2E.TraitBulwark", "Bulwark") });
  }
  return modifiers;
}
